import { Volume2 } from "lucide-react";
import { Sounds } from "../../lib/sound";
import { POWERUP_INFO, LEGEND_PREVIEW_SFX } from "../engine/catalog";

/**
 * PowerupLegendModal — extracted from GameCanvas.jsx.
 *
 * Self-contained help overlay listing every power-up drop + the hidden
 * Bone Coin bonus, with click-to-preview SFX buttons that fire the
 * exact same audio cue the game plays on pickup.
 *
 * Props are intentionally minimal: just `open` + `onClose` + `query`
 * state. Filter input is controlled from the parent so the open/close
 * cycle preserves the query (which matters when a pilot scrolls past
 * results, closes by mistake, and reopens).
 *
 * No canvas refs, no game state, no hooks — pure JSX of the modal
 * tree. Extracted as part of the 2026-05-22 GameCanvas slim-down to
 * keep the engine file focused on the actual game loop.
 */
export default function PowerupLegendModal({
  open,
  onClose,
  query,
  onQueryChange,
}) {
  if (!open) return null;

  const q = (query || "").trim().toLowerCase();
  const entries = Object.entries(POWERUP_INFO).filter(([kind, info]) => {
    if (!q) return true;
    return (
      kind.toLowerCase().includes(q)
      || info.label.toLowerCase().includes(q)
      || info.desc.toLowerCase().includes(q)
    );
  });
  const showBoneCoin = !q
    || "bone coin hidden bonus skeleton worms moonstones multi-ball perfect clear".includes(q);
  const noMatches = entries.length === 0 && !showBoneCoin;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="powerup-legend-modal"
    >
      <div
        className="glass p-6 border border-[#b026ff]/50 max-w-md w-full"
        style={{ boxShadow: "0 0 24px rgba(176,38,255,0.3)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase mb-1">
              // POWER-UP LEGEND
            </div>
            <div className="font-heading font-black uppercase text-2xl tracking-tight text-white">
              Drops & Effects
            </div>
          </div>
          <button
            onClick={onClose}
            className="font-mono-cosmic text-[10px] tracking-widest uppercase text-white/60 hover:text-white border border-white/20 hover:border-white/50 px-2 py-1"
            data-testid="legend-close-btn"
          >
            Close
          </button>
        </div>
        {/* Filter input — lets pilots jump straight to "bone", "wide",
            "slow" etc. as the legend grows. */}
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Filter drops & effects…"
          aria-label="Filter legend entries"
          data-testid="legend-filter-input"
          className="w-full mb-3 px-3 py-2 bg-black/50 border border-white/15 focus:border-[#b026ff]/80 focus:outline-none text-white/90 placeholder:text-white/35 font-mono-cosmic text-xs tracking-wide transition"
        />
        <div className="grid grid-cols-1 gap-2">
          {entries.map(([kind, info]) => (
            <div
              key={kind}
              className="flex items-center gap-3 px-3 py-2 border bg-black/40"
              style={{ borderColor: `${info.color}55` }}
            >
              <span
                className="inline-flex items-center justify-center w-16 font-mono-cosmic text-[10px] tracking-widest uppercase py-1 border"
                style={{
                  color: info.color,
                  borderColor: `${info.color}aa`,
                  background: `${info.color}18`,
                }}
              >
                {info.label.split(" ")[0].slice(0, 5).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-heading uppercase text-sm tracking-tight text-white">
                  {info.label}
                </div>
                <div className="text-white/55 text-xs leading-snug">{info.desc}</div>
              </div>
              <span className="font-mono-cosmic text-[9px] tracking-widest text-white/40">
                {(info.duration / 1000).toFixed(0)}s
              </span>
              {/* Preview speaker — fires the same SFX the game plays on
                  pickup. Stops event propagation so clicking the
                  button doesn't dismiss the modal (backdrop closes on
                  click). */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const sfx = LEGEND_PREVIEW_SFX[kind];
                  if (sfx && typeof Sounds[sfx] === "function") Sounds[sfx]();
                }}
                className="flex-shrink-0 p-1.5 border border-white/15 hover:border-white/50 text-white/55 hover:text-white transition"
                data-testid={`legend-preview-${kind}`}
                aria-label={`Preview ${info.label} sound`}
                title={`Preview ${info.label} sound`}
              >
                <Volume2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        {entries.length > 0 && (
          <p className="text-[11px] text-white/40 mt-4 font-mono-cosmic tracking-wide">
            Drops appear after ~22% of block kills. Rarity weighting applies.
          </p>
        )}
        {showBoneCoin && (
          <div
            className={`${entries.length > 0 ? "mt-3" : "mt-0"} px-3 py-2 border border-[#aef0ff]/40 bg-[#aef0ff]/10 flex items-start gap-2`}
            data-testid="legend-bone-coin-hint"
          >
            <span
              className="font-mono-cosmic text-[14px] leading-none mt-0.5"
              style={{
                color: "#dde3eb",
                filter: "drop-shadow(0 0 3px rgba(174,240,255,0.7))",
              }}
              aria-hidden="true"
            >
              ☠
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-heading uppercase text-xs tracking-tight text-[#aef0ff]">
                Bone Coin · Hidden Bonus
              </div>
              <div className="text-white/55 text-[11px] leading-snug">
                Kill all 3 Skeleton Worms cleanly on the same life for a one-time reward — 5 moonstones in Endless or a guaranteed Multi-Ball in regular play.
              </div>
            </div>
            {/* Two previews: bone-rattle hit + cracked-skull kill. */}
            <div className="flex-shrink-0 flex flex-col gap-1">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); Sounds.skeletonHit(); }}
                className="p-1 border border-[#aef0ff]/25 hover:border-[#aef0ff]/80 text-[#aef0ff]/70 hover:text-[#aef0ff] transition flex items-center gap-1"
                data-testid="legend-preview-skeleton-hit"
                aria-label="Preview skeleton hit sound"
                title="Preview skeleton-hit (rattle)"
              >
                <Volume2 className="w-3 h-3" />
                <span className="font-mono-cosmic text-[8px] tracking-[0.15em] uppercase">hit</span>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); Sounds.skeletonKill(); }}
                className="p-1 border border-[#aef0ff]/25 hover:border-[#aef0ff]/80 text-[#aef0ff]/70 hover:text-[#aef0ff] transition flex items-center gap-1"
                data-testid="legend-preview-skeleton-kill"
                aria-label="Preview skeleton kill sound"
                title="Preview skeleton-kill (crack)"
              >
                <Volume2 className="w-3 h-3" />
                <span className="font-mono-cosmic text-[8px] tracking-[0.15em] uppercase">kill</span>
              </button>
            </div>
          </div>
        )}
        {noMatches && (
          <div
            className="text-center py-6 font-mono-cosmic text-[11px] tracking-[0.25em] uppercase text-white/40"
            data-testid="legend-no-matches"
          >
            No drops match "{query}"
          </div>
        )}
      </div>
    </div>
  );
}
